import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getMyAgencies, createMyAgency, updateMyAgency, deleteMyAgency, setDefaultAgency } from '../services/api';
import { toast } from 'sonner';
import { Building2, Plus, Pencil, Trash2, Star, MapPin, Phone, Mail, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { motion } from 'framer-motion';

const SettingsPage = () => {
  const { user, refreshUser } = useAuth();
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({ name: '', address: '', zip_code: '', city: '', phone: '', email: '' });

  useEffect(() => { fetchAgencies(); }, []);

  const fetchAgencies = async () => {
    try {
      const res = await getMyAgencies();
      setAgencies(res.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setFormData({ name: '', address: '', zip_code: '', city: '', phone: '', email: '' });
    setDialogOpen(true);
  };

  const openEdit = (agency) => {
    setEditing(agency);
    setFormData({
      name: agency.name || '',
      address: agency.address || '',
      zip_code: agency.zip_code || '',
      city: agency.city || '',
      phone: agency.phone || '',
      email: agency.email || '',
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editing) {
        await updateMyAgency(editing.id, formData);
        toast.success('Agence modifiée');
      } else {
        await createMyAgency(formData);
        toast.success('Agence créée');
      }
      setDialogOpen(false);
      setEditing(null);
      fetchAgencies();
      if (refreshUser) await refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette agence ?')) return;
    try {
      await deleteMyAgency(id);
      toast.success('Agence supprimée');
      fetchAgencies();
      if (refreshUser) await refreshUser();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleSetDefault = async (id) => {
    try {
      await setDefaultAgency(id);
      toast.success('Agence par défaut mise à jour');
      if (refreshUser) await refreshUser();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  if (loading) {
    return (
      <div className="max-w-3xl space-y-4">
        <Skeleton className="h-10 w-48 bg-white" />
        {[...Array(2)].map((_, i) => <Skeleton key={i} className="h-32 bg-white" />)}
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-6" data-testid="settings-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#191D1E]">Mes agences</h1>
          <p className="text-sm text-[#687175] mt-1">
            L'adresse de l'agence par défaut apparaît dans les e-mails envoyés à vos clients
          </p>
        </div>
        <Button className="btn-primary" onClick={openCreate} data-testid="add-agency-btn">
          <Plus className="w-4 h-4 mr-2" /> Nouvelle agence
        </Button>
      </div>

      {agencies.length === 0 ? (
        <div className="bg-white border border-[#BFC6CC]/12 rounded-2xl p-12 text-center">
          <Building2 className="w-12 h-12 text-[#BFC6CC] mx-auto mb-4" />
          <h3 className="font-bold text-[#191D1E] mb-1">Pas encore d'agence</h3>
          <p className="text-sm text-[#687175]">Créez votre première agence pour personnaliser vos e-mails.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {agencies.map((agency, index) => {
            const isDefault = user?.agency_id === agency.id;
            return (
              <motion.div
                key={agency.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`bg-white border rounded-2xl p-5 transition-shadow hover:shadow-md ${
                  isDefault ? 'border-[#397DFF] ring-1 ring-[#397DFF]/20' : 'border-[#BFC6CC]/12'
                }`}
                data-testid={`agency-card-${agency.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      isDefault ? 'bg-[#397DFF]/10' : 'bg-[#F5F6F9]'
                    }`}>
                      <Building2 className={`w-5 h-5 ${isDefault ? 'text-[#397DFF]' : 'text-[#939BA0]'}`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold text-[#191D1E]">{agency.name}</h3>
                        {isDefault && (
                          <span className="text-[10px] font-semibold bg-[#397DFF]/10 text-[#397DFF] px-2 py-0.5 rounded-full">
                            PAR DÉFAUT
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {!isDefault && (
                      <button onClick={() => handleSetDefault(agency.id)}
                        className="p-2 text-[#939BA0] hover:text-[#397DFF] hover:bg-[#397DFF]/10 rounded-lg transition-colors"
                        title="Définir par défaut" data-testid={`set-default-${agency.id}`}>
                        <Star className="w-4 h-4" />
                      </button>
                    )}
                    <button onClick={() => openEdit(agency)}
                      className="p-2 text-[#397DFF] hover:bg-[#397DFF]/10 rounded-lg transition-colors"
                      title="Modifier" data-testid={`edit-agency-${agency.id}`}>
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDelete(agency.id)}
                      className="p-2 text-[#939BA0] hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      title="Supprimer" data-testid={`delete-agency-${agency.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm text-[#687175]">
                  {(agency.address || agency.city) && (
                    <div className="flex items-center gap-1.5">
                      <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                      <span>{agency.address ? `${agency.address}, ` : ''}{agency.zip_code} {agency.city}</span>
                    </div>
                  )}
                  {agency.phone && (
                    <div className="flex items-center gap-1.5">
                      <Phone className="w-3.5 h-3.5 flex-shrink-0" />
                      <span>{agency.phone}</span>
                    </div>
                  )}
                  {agency.email && (
                    <div className="flex items-center gap-1.5">
                      <Mail className="w-3.5 h-3.5 flex-shrink-0" />
                      <span>{agency.email}</span>
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Dialog Create/Edit */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) setEditing(null); }}>
        <DialogContent className="bg-white border-[#BFC6CC]/30">
          <DialogHeader>
            <DialogTitle className="text-[#191D1E]">
              {editing ? 'Modifier l\'agence' : 'Nouvelle agence'}
            </DialogTitle>
            <DialogDescription className="text-[#687175]">
              {editing ? 'Modifiez les informations de votre agence.' : 'Renseignez les informations de votre nouvelle agence.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <Label className="text-[#191D1E]">Nom de l'agence</Label>
              <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="mt-1.5 bg-[#F5F6F9] border-[#BFC6CC] text-[#191D1E]" placeholder="Agence Bordeaux" required data-testid="agency-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[#191D1E]">Adresse</Label>
                <Input value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="mt-1.5 bg-[#F5F6F9] border-[#BFC6CC] text-[#191D1E]" placeholder="12 rue de la Paix" data-testid="agency-address-input" />
              </div>
              <div>
                <Label className="text-[#191D1E]">Ville</Label>
                <Input value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="mt-1.5 bg-[#F5F6F9] border-[#BFC6CC] text-[#191D1E]" placeholder="Bordeaux" data-testid="agency-city-input" />
              </div>
            </div>
            <div>
              <Label className="text-[#191D1E]">Code postal</Label>
              <Input value={formData.zip_code} onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                className="mt-1.5 bg-[#F5F6F9] border-[#BFC6CC] text-[#191D1E]" placeholder="33000" data-testid="agency-zip-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[#191D1E]">Téléphone</Label>
                <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="mt-1.5 bg-[#F5F6F9] border-[#BFC6CC] text-[#191D1E]" placeholder="09 80 80 23 80" data-testid="agency-phone-input" />
              </div>
              <div>
                <Label className="text-[#191D1E]">Email</Label>
                <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="mt-1.5 bg-[#F5F6F9] border-[#BFC6CC] text-[#191D1E]" placeholder="agence@exemple.com" data-testid="agency-email-input" />
              </div>
            </div>
            <Button type="submit" className="w-full btn-primary" disabled={submitting} data-testid="agency-submit-btn">
              {submitting ? 'En cours...' : (editing ? 'Enregistrer' : 'Créer l\'agence')}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SettingsPage;
